import { ChevronDown, Pencil, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { FlatfinderPerspective } from 'realitiq-utils/src/flatfinder';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export function FlatfinderPerspectiveControls({
  busy,
  canEdit,
  perspective,
  perspectives,
  onAdd,
  onEdit,
  onRemove,
  onSelect,
}: {
  busy: boolean;
  canEdit: boolean;
  perspective?: FlatfinderPerspective;
  perspectives: FlatfinderPerspective[];
  onAdd: () => void;
  onEdit: () => void;
  onRemove: () => void;
  onSelect: (perspectiveId: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex w-52 min-w-0 flex-col gap-1 rounded-sm border bg-white p-1">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="ghost"
            disabled={!perspective || busy}
            aria-label={t('flatfinderWorkspaceSwitchPerspective')}
            className="!h-7 min-h-7 w-full max-w-none justify-between gap-1 border border-primary-dark/20 bg-white px-2 text-xs font-medium text-text-primary hover:border-primary-dark/40 hover:bg-primary-dark/5 lg:px-2 [&_svg]:size-4"
          >
            <span className="truncate">{perspective?.name ?? t('perspective')}</span>
            <ChevronDown className="ml-auto shrink-0" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)]">
          {perspectives.map((item) => (
            <DropdownMenuItem key={item.id} onSelect={() => onSelect(item.id)}>
              {item.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <div className="grid grid-cols-3 gap-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-full min-w-0 gap-1 px-1 py-0 lg:px-1 [&_svg]:size-4"
          aria-label={t('flatfinderWorkspaceEditPerspective')}
          title={t('flatfinderWorkspaceEditPerspective')}
          disabled={busy || !perspective || !canEdit}
          onClick={onEdit}
        >
          <Pencil />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-full min-w-0 gap-1 px-1 py-0 lg:px-1 [&_svg]:size-4"
          aria-label={t('flatfinderWorkspaceRemovePerspective')}
          title={t('flatfinderWorkspaceRemovePerspective')}
          disabled={busy || !perspective || !canEdit}
          onClick={onRemove}
        >
          <Trash2 />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-full min-w-0 gap-1 px-1 py-0 lg:px-1 [&_svg]:size-4"
          aria-label={t('flatfinderWorkspaceAddPerspective')}
          title={t('flatfinderWorkspaceAddPerspective')}
          disabled={busy || !canEdit}
          onClick={onAdd}
        >
          <Plus />
          <span className="text-xs">{t('new')}</span>
        </Button>
      </div>
    </div>
  );
}
